/** @jsx jsx */
import { Fragment, useEffect, useState } from 'react'
import { Box, Button, Flex, jsx, Text } from 'theme-ui'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'
import ChevronIcon from '~/assets/images/icons/chevron.inline.svg'

const PagerButton = ({ isActive, onClick, page }) => {
  return (
    <Button
      aria-label={`Pager page ${page}`}
      onClick={() => onClick(page)}
      variant="reset"
      sx={{
        backgroundColor: isActive ? 'brandPrimary' : 'backgroundDark',
        borderRadius: '50%',
        height: '40px',
        width: '40px',
        '&:hover': {
          backgroundColor: 'brandPrimary',
          span: {
            color: 'white',
          },
        },
      }}
    >
      <Text
        variant="textLink"
        color={isActive ? 'white' : 'brandPrimary'}
        sx={{
          position: 'relative',
          top: '-1px',
          '&:hover': { textDecoration: 'none' },
        }}
      >
        {page}
      </Text>
    </Button>
  )
}

const PagerArrow = ({ direction, disabled, onClick, page }) => {
  return (
    <Button
      variant="reset"
      disabled={disabled}
      onClick={() => onClick(page)}
      sx={{
        backgroundColor: !disabled ? 'white' : 'transparent',
        border: '1px solid',
        borderColor: 'greyMedium',
        borderRadius: '10px',
        height: '40px',
        width: '40px',
      }}
    >
      <VisuallyHidden>{direction}</VisuallyHidden>
      <ChevronIcon
        sx={{
          color: !disabled ? 'black' : 'greyMedium',
          pointerEvents: 'none',
          position: 'relative',
          top: '-3px',
          transform:
            direction === 'previous' ? 'rotate(90deg)' : 'rotate(-90deg)',
        }}
      />
    </Button>
  )
}

const PagerDots = () => {
  return (
    <Text
      as="p"
      m={0}
      variant="textLink"
      sx={{
        color: 'black',
        height: '40px',
        textAlign: 'center',
        width: '20px',
        '&:hover': { textDecoration: 'none' },
      }}
    >
      <Text sx={{ position: 'relative', top: '5px' }}>...</Text>
    </Text>
  )
}

const Pager = ({ currentPage, onPagerClick, totalPages }) => {
  const firstPage = 1
  const [buttonsToShow, setButtonsToShow] = useState([])

  useEffect(() => {
    const buttonCounts = [3, 5, 7]

    setButtonsToShow(
      buttonCounts.map((count) => {
        const bounds = Math.floor(count / 2)
        let list = []

        for (let i = currentPage - bounds; i <= currentPage + bounds; i++) {
          list.push(i)
        }

        // Account for outer boundaries.
        if (list[0] < 1) {
          list = list.map((item) => item + (1 - list[0]))
        } else if (totalPages < list[list.length - 1]) {
          list = list.map((item) => item - (list[list.length - 1] - totalPages))
        }

        return list
      })
    )
  }, [currentPage, totalPages])

  const mx = '4px'

  if (totalPages <= 1) {
    return <Fragment />
  }

  return (
    <Flex sx={{ justifyContent: 'center' }}>
      <Box mx={mx}>
        <PagerArrow
          direction="previous"
          disabled={currentPage === firstPage}
          onClick={onPagerClick}
          page={currentPage - 1}
        />
      </Box>
      <Box
        mx={mx}
        sx={{
          display: buttonsToShow.map((bp) =>
            !bp.includes(firstPage) ? 'block' : 'none'
          ),
        }}
      >
        <PagerDots />
      </Box>
      {Array.from(Array(totalPages).keys())
        .map((i) => i + 1)
        .map((page) => (
          <Box
            key={`page-button-${page}`}
            mx={mx}
            sx={{
              display: buttonsToShow.map((bp) =>
                bp.includes(page) ? 'block' : 'none'
              ),
            }}
          >
            <PagerButton
              isActive={page === currentPage}
              onClick={onPagerClick}
              page={page}
            />
          </Box>
        ))}
      <Box
        mx={mx}
        sx={{
          display: buttonsToShow.map((bp) =>
            !bp.includes(totalPages) ? 'block' : 'none'
          ),
        }}
      >
        <PagerDots />
      </Box>
      <Box mx={mx}>
        <PagerArrow
          direction="next"
          disabled={currentPage === totalPages}
          onClick={onPagerClick}
          page={currentPage + 1}
        />
      </Box>
    </Flex>
  )
}

export default Pager
